import { readlink } from "node:fs/promises";

import { initServiceStructure } from "./init-structure.js";
import { appendDeployLog, deployNotice } from "./log.js";
import { pullRepo } from "./git.js";
import { createRelease } from "./release.js";
import { installDeps } from "./deps.js";
import { runReleaseTests } from "./test.js";
import { activateRelease } from "./activate.js";
import { pruneReleases } from "./prune.js";
import { reloadPm2, stopPm2, startPm2 } from "./pm2.js";
import { enableNginxUpstream } from "./nginx.js";
import { syncEnv } from "./sync-env.js";
import { writeReleaseBuildInfo } from "./build-info.js";
import { servicePaths } from "./paths.js";

/**
 * Resolve pm2 kill_timeout (ms). Prefer manifest `pm2.killTimeout`, else
 * stopAllowance seconds × 1000 + 5s buffer, else 65s.
 */
function resolveKillTimeoutMs(service) {
    const explicit = Number(service.pm2?.killTimeout);
    if (Number.isFinite(explicit) && explicit > 0) return Math.floor(explicit);
    const stopSec = Number(service.pm2?.stopAllowance);
    if (Number.isFinite(stopSec) && stopSec > 0) return Math.floor(stopSec * 1000) + 5000;
    return 65_000;
}

/**
 * Full deploy: pull → env → release → deps → test → activate → pm2 → prune → nginx.
 *
 * Default (**rolling**): keep the process running while the new release is built and
 * `current` is flipped; then `pm2 delete` + `pm2 start` (SIGINT + kill_timeout drain,
 * then full ecosystem recreate) so script/node_args/env always apply. Previous
 * release is not pruned until after that restart.
 *
 * **`--stopFirst`**: `pm2 stop` + wait → activate/prune → delete + `pm2 start`.
 * Use when a runner is wedged and will not honor graceful stop.
 */
export async function deployService(service, options = {}) {
    const {
        dryRun = false,
        skipPull = false,
        skipTests = false,
        skipNginx = false,
        stopFirst = false,
        logger = console,
    } = options;

    const paths = servicePaths(service);
    const appName = service.pm2.appName;
    const killTimeoutMs = resolveKillTimeoutMs(service);
    const waitTimeoutMs = killTimeoutMs + 10_000;

    deployNotice(logger, `Prepare ${service.name} directories`);
    await initServiceStructure(service, { dryRun, logger });

    if (!skipPull) {
        deployNotice(logger, "Pull latest repository");
        await pullRepo(service, { dryRun, logger });
    }

    deployNotice(logger, "Sync environment");
    await syncEnv(service, { dryRun, logger });

    deployNotice(logger, "Create release");
    const { stamp, path: releasePath } = await createRelease(service, { dryRun, logger });
    await writeReleaseBuildInfo(service, releasePath, { stamp, dryRun, logger });

    deployNotice(logger, "Install dependencies");
    await installDeps(service, releasePath, paths, { dryRun, logger });

    if (!skipTests) {
        deployNotice(logger, "Run release tests");
        await runReleaseTests(service, releasePath, paths, { dryRun, logger });
    }

    let previousReleaseName = null;
    try {
        const prev = await readlink(paths.current);
        previousReleaseName = prev.split("/").pop() || null;
    } catch {
        // first deploy
    }

    if (stopFirst) {
        logger.info(`deploy mode=stopFirst app=${appName} killTimeoutMs=${killTimeoutMs}`);
        deployNotice(logger, `Stop ${appName}`);
        await stopPm2(appName, { dryRun, logger, waitTimeoutMs });
        deployNotice(logger, "Activate release");
        await activateRelease(releasePath, paths, { dryRun, logger });
        deployNotice(logger, "Prune old releases");
        await pruneReleases(service, paths, { dryRun, logger });
        deployNotice(logger, "Start pm2");
        await startPm2(paths, { dryRun, logger, appName, waitTimeoutMs });
    } else {
        logger.info(
            `deploy mode=rolling app=${appName} killTimeoutMs=${killTimeoutMs}` +
                (previousReleaseName ? ` previous=${previousReleaseName}` : "")
        );
        deployNotice(logger, "Activate release");
        await activateRelease(releasePath, paths, { dryRun, logger });
        // Restart into new `current` before pruning the previous tree (still in use by the old process).
        deployNotice(logger, "Reload pm2");
        await reloadPm2(paths, { dryRun, logger, appName, waitTimeoutMs });
        deployNotice(logger, "Prune old releases");
        await pruneReleases(service, paths, { dryRun, logger });
    }

    if (!skipNginx) {
        deployNotice(logger, "Update nginx upstream");
        await enableNginxUpstream(service, { dryRun, logger });
    }

    const summary = `deploy complete stamp=${stamp} mode=${stopFirst ? "stopFirst" : "rolling"} dryRun=${dryRun}`;
    deployNotice(logger, `Deploy complete (${stamp})`);
    logger.info(summary);
    if (!dryRun) await appendDeployLog(paths.deployLog, summary);

    return { stamp, releasePath, mode: stopFirst ? "stopFirst" : "rolling" };
}
